/**
 * The four post-creation warning families of file/CreateDocumentFromTemplate, as the
 * host's persistent UnfilledPlaceholdersMessageBar (or the toast fallback) consumes them.
 * Always fully populated with arrays - an EMPTY list is meaningful (it clears the bar of an
 * earlier creation), so nothing here is ever undefined.
 */
export interface ICreateFromTemplateWarnings {
    /** Custom fields the server could not fill ("name (type)"). */
    unfilledPlaceholders: string[];
    /** Provided custom values that matched no live token (stale scan, renamed field). */
    unusedCustomValues: string[];
    /** Placeholder-shaped tokens that survived the fill (column tokens AND unresolved CRM tokens). */
    unfilledColumnPlaceholders: string[];
    /** Per-family CRM resolution failure reasons (fortes/simplicate/exact), non-empty only. */
    crmResolutionReasons: string[];
}

const asStringList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/**
 * Lifts the warning families off a creation response. The response is the ONLY input: this
 * renders what the server sent and never decides for itself what to hide.
 *
 * That is what implements the tenant's "Disable unfilled placeholder warnings" setting. The
 * server reads the flag per creation (TenantPlaceholderWarningPolicy) and nulls all six
 * warning properties, which the serializer then omits from the wire; every family here comes
 * back empty and the bar renders nothing. There is deliberately no client-side copy of the
 * flag: the add-in's tenant info is a startup snapshot with no refresh, so gating on it would
 * keep suppressing warnings after an admin switched the setting back off.
 */
export const extractCreateFromTemplateWarnings = (response: any): ICreateFromTemplateWarnings => ({
    unfilledPlaceholders: asStringList(response?.unfilledCustomFields),
    unusedCustomValues: asStringList(response?.unusedCustomValues),
    unfilledColumnPlaceholders: asStringList(response?.unfilledColumnPlaceholders),
    // null / omitted on the wire means that family either resolved, had no tokens, or was
    // stripped by the tenant's opt-out.
    crmResolutionReasons: [response?.fortesResolution, response?.simplicateResolution, response?.exactResolution]
        .filter((reason): reason is string => typeof reason === "string" && reason.trim().length > 0),
});
